import { apiClient } from "@/config/api";
import {
  Actor,
  CreateActorDto,
  UpdateActorDto,
  GetPaginationActorsDto,
  ActorListResponse,
  ExternalActor,
} from "@/types/actor";
import { ApiResponse } from "@/types/status";

const ACTOR_ENDPOINTS = {
  CREATE: "/actors",
  CREATE_WITH_IMAGE: "/actors/upload",
  GET_ALL: "/actors",
  GET_BY_ID: (id: string) => `/actors/${id}`,
  GET_BY_SLUG: (slug: string) => `/actors/slug/${slug}`,
  UPDATE: (id: string) => `/actors/${id}`,
  UPDATE_WITH_IMAGE: (id: string) => `/actors/${id}/upload`,
  DELETE: (id: string) => `/actors/${id}`,
  SYNC_BULK: "/actors/sync/bulk",
  SYNC_SINGLE: (externalId: string) => `/actors/sync/${externalId}`,
  GET_EXTERNAL: "/actors/external",
  GET_COUNTRIES: "/external/countries",
} as const;

export const actorService = {
  // Tạo diễn viên mới
  create: async (data: CreateActorDto): Promise<ApiResponse<Actor>> => {
    return await apiClient.post(ACTOR_ENDPOINTS.CREATE, data);
  },

  // Lấy danh sách diễn viên với phân trang
  getPaginationActors: async (
    params: GetPaginationActorsDto
  ): Promise<ApiResponse<ActorListResponse>> => {
    return await apiClient.get(ACTOR_ENDPOINTS.GET_ALL, params as any);
  },

  // Lấy tất cả diễn viên
  getAllActors: async (): Promise<ApiResponse<Actor[]>> => {
    return await apiClient.get(ACTOR_ENDPOINTS.GET_ALL);
  },

  // Lấy diễn viên theo ID
  getActorById: async (id: string): Promise<ApiResponse<Actor>> => {
    return await apiClient.get(ACTOR_ENDPOINTS.GET_BY_ID(id));
  },

  // Lấy diễn viên theo slug
  getActorBySlug: async (slug: string): Promise<ApiResponse<Actor>> => {
    return await apiClient.get(ACTOR_ENDPOINTS.GET_BY_SLUG(slug));
  },

  // Cập nhật diễn viên (SEO fields)
  updateActor: async (
    id: string,
    data: UpdateActorDto
  ): Promise<ApiResponse<Actor>> => {
    return await apiClient.put(ACTOR_ENDPOINTS.UPDATE(id), data);
  },

  // Xóa diễn viên
  deleteActor: async (id: string): Promise<ApiResponse<void>> => {
    return await apiClient.delete(ACTOR_ENDPOINTS.DELETE(id));
  },

  // Sync tất cả diễn viên từ external DB
  syncBulk: async (): Promise<
    ApiResponse<{ totalProcessed: number; created: number; updated: number }>
  > => {
    return await apiClient.post(ACTOR_ENDPOINTS.SYNC_BULK);
  },

  // Sync một diễn viên từ external DB
  syncSingle: async (externalId: string): Promise<ApiResponse<Actor>> => {
    return await apiClient.post(ACTOR_ENDPOINTS.SYNC_SINGLE(externalId));
  },

  // Lấy danh sách diễn viên từ external DB
  getExternalActors: async (): Promise<ApiResponse<ExternalActor[]>> => {
    return await apiClient.get(ACTOR_ENDPOINTS.GET_EXTERNAL);
  },

  // Tạo diễn viên với upload hình ảnh
  createWithImage: async (
    data: CreateActorDto,
    imageFile?: File
  ): Promise<ApiResponse<Actor>> => {
    const formData = new FormData();

    // Required fields
    formData.append("name", data.name);
    formData.append("url", data.url);

    // Optional fields
    if (data.description) formData.append("description", data.description);
    if (data.birthday) formData.append("birthday", data.birthday);
    if (data.countryName) formData.append("countryName", data.countryName);
    if (data.height !== undefined && data.height !== null)
      formData.append("height", String(data.height));
    if (data.seoTitle) formData.append("seoTitle", data.seoTitle);
    if (data.metaDescription)
      formData.append("metaDescription", data.metaDescription);

    // Image file or URL
    if (imageFile) {
      formData.append("image", imageFile);
    } else if (data.image_url) {
      formData.append("image_url", data.image_url);
    }

    return await apiClient.post(ACTOR_ENDPOINTS.CREATE_WITH_IMAGE, formData);
  },

  // Cập nhật diễn viên với upload hình ảnh
  updateWithImage: async (
    id: string,
    data: UpdateActorDto,
    imageFile?: File
  ): Promise<ApiResponse<Actor>> => {
    const formData = new FormData();

    // Add all provided fields
    if (data.name) formData.append("name", data.name);
    if (data.url) formData.append("url", data.url);
    if (data.description !== undefined)
      formData.append("description", data.description);
    if (data.birthday !== undefined) formData.append("birthday", data.birthday);
    if (data.countryName !== undefined)
      formData.append("countryName", data.countryName);
    if (data.height !== undefined && data.height !== null)
      formData.append("height", String(data.height));
    if (data.seoTitle !== undefined) formData.append("seoTitle", data.seoTitle);
    if (data.metaDescription !== undefined)
      formData.append("metaDescription", data.metaDescription);

    // Image file or URL
    if (imageFile) {
      formData.append("image", imageFile);
    } else if (data.image_url !== undefined) {
      formData.append("image_url", data.image_url);
    }

    return await apiClient.put(ACTOR_ENDPOINTS.UPDATE_WITH_IMAGE(id), formData);
  },

  // Lấy danh sách quốc gia
  getCountries: async (): Promise<
    ApiResponse<{ _id: string; name: string }[]>
  > => {
    return await apiClient.get(ACTOR_ENDPOINTS.GET_COUNTRIES);
  },
};
